<template>
    <div class="card p-shadow-app">
        <h1>Propostas Enviadas</h1>
        <Toast />

        <div class="p-fluid p-formgrid p-grid p-mb-3">

            <div class="p-field p-col-12 p-md-2">
                <label for="obrano">Nº Proposta</label>
                <InputText
                    id="obrano"
                    v-model="filters.obrano"
                    placeholder="Filtrar por nº de proposta"
                />
            </div>

            <div class="p-field p-col-12 p-md-2">
                <label for="pat_nopat">Nº PAT</label>
                <InputText
                    id="pat_nopat"
                    v-model="filters.pat_nopat"
                    placeholder="Filtrar por nº de PAT"
                />
            </div>

            <div class="p-field p-col-12 p-md-3">
                <label for="recipient">Destinatário</label>
                <InputText
                    id="recipient"
                    v-model="filters.recipient"
                    placeholder="Email do cliente"
                />
            </div>

            <div class="p-field p-col-12 p-md-2">
                <label for="status">Estado</label>
                <Dropdown
                    id="status"
                    v-model="filters.status"
                    :options="statusOptions"
                    optionLabel="label"
                    optionValue="value"
                    placeholder="Todos"
                    :showClear="true"
                    class="w-full"
                />
            </div>

            <div class="p-field p-col-12 p-md-2 p-d-flex p-ai-end">
                <Button
                    label="Aplicar Filtros"
                    icon="pi pi-filter"
                    @click="fetchWithFilters"
                />
            </div>

            <div class="p-field p-col-12 p-md-1 p-d-flex p-ai-end">
                <Button
                    label="Limpar"
                    icon="pi pi-times"
                    class="p-button-secondary p-button-text"
                    @click="clearStoredFilters"
                />
            </div>
        </div>

        <DataTable
            :value="list"
            :loading="loading"
            :paginator="true"
            :rows="perPage"
            :rowsPerPageOptions="[10, 20, 50, 100]"
            responsiveLayout="scroll"
            class="p-datatable-sm"
            stripedRows
            :lazy="true"
            :totalRecords="totalRecords"
            @page="fetchData"
            paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
            currentPageReportTemplate="Mostrar de {first} a {last} de {totalRecords} dados"
        >
            <template #empty>
                <div class="p-text-center p-p-3">
                    <i class="pi pi-inbox" style="font-size: 3rem; color: #999;"></i>
                    <p class="p-mt-2">Nenhuma proposta encontrada</p>
                </div>
            </template>

            <Column field="obrano" header="Nº Proposta" style="min-width: 110px">
                <template #body="slotProps">
                    <strong>{{ slotProps.data.obrano }}</strong>
                </template>
            </Column>

            <Column field="pat_nopat" header="PAT" style="min-width: 100px">
                <template #body="slotProps">
                    <span
                        v-if="slotProps.data.pat_nopat"
                        class="clickable-item"
                        v-tooltip="'Ver PAT'"
                        @click="goToPat(slotProps.data.pat_nopat)"
                    >
                        <strong>{{ slotProps.data.pat_nopat }}</strong>
                    </span>
                    <span v-else>-</span>
                </template>
            </Column>

            <Column field="recipient_email" header="Email" style="min-width: 200px">
                <template #body="slotProps">
                    {{ slotProps.data.recipient_email || '-' }}
                </template>
            </Column>

            <Column field="recipient_phone" header="SMS" style="min-width: 130px">
                <template #body="slotProps">
                    {{ slotProps.data.recipient_phone || '-' }}
                </template>
            </Column>

            <Column field="sent_at" header="Enviada em" style="min-width: 130px">
                <template #body="slotProps">
                    {{ formatDateTime(slotProps.data.sent_at) }}
                </template>
            </Column>

            <Column field="expires_at" header="Expira em" style="min-width: 130px">
                <template #body="slotProps">
                    {{ formatDateTime(slotProps.data.expires_at) }}
                </template>
            </Column>

            <Column field="reminder_sent_at" header="Lembrete" style="min-width: 130px">
                <template #body="slotProps">
                    {{ formatDateTime(slotProps.data.reminder_sent_at) }}
                </template>
            </Column>

            <Column field="status" header="Estado" style="min-width: 130px">
                <template #body="slotProps">
                    <span
                        class="p-badge p-component"
                        :style="{ backgroundColor: statusColor(slotProps.data.status), color: '#fff' }"
                    >
                        {{ statusLabel(slotProps.data.status) }}
                    </span>
                </template>
            </Column>

            <Column field="response" header="Resposta" style="min-width: 110px">
                <template #body="slotProps">
                    <span v-if="slotProps.data.response === true" class="p-tag p-tag-success">Aceite</span>
                    <span v-else-if="slotProps.data.response === false" class="p-tag p-tag-danger">Recusada</span>
                    <span v-else>-</span>
                </template>
            </Column>

            <Column header="Ações" style="min-width: 180px">
                <template #body="slotProps">
                    <div class="p-d-flex p-gap-2">
                        <Button
                            icon="pi pi-eye"
                            class="p-button-rounded p-button-info p-button-text"
                            v-tooltip.top="'Ver Detalhes'"
                            @click="viewDetails(slotProps.data)"
                        />
                        <a
                            v-if="slotProps.data.hash"
                            :href="getValidationUrl(slotProps.data.hash)"
                            target="_blank"
                            class="p-button p-component p-button-icon-only p-button-rounded p-button-warning p-button-text"
                            v-tooltip.top="'Abrir Link de Validação'"
                        >
                            <span class="p-button-icon pi pi-external-link"></span>
                        </a>
                        <Button
                            v-if="canExpire(slotProps.data)"
                            icon="pi pi-times-circle"
                            class="p-button-rounded p-button-danger p-button-text"
                            v-tooltip.top="'Forçar expiração'"
                            @click="askForExpire(slotProps.data)"
                        />
                    </div>
                </template>
            </Column>
        </DataTable>

        <ConfirmDialog group="commercialOfferDispatchesConfirmDialog" />

        <Dialog
            :visible.sync="detailsDialog"
            :style="{ width: '700px' }"
            :header="`Detalhes — Proposta #${selectedRow ? selectedRow.obrano : ''}`"
            :modal="true"
            :maximizable="true"
        >
            <div v-if="selectedRow" class="p-fluid">
                <dl>
                    <dt>Nº Proposta:</dt>
                    <dd><strong>{{ selectedRow.obrano }}</strong></dd>
                    <dt>PAT:</dt>
                    <dd>{{ selectedRow.pat_nopat || '— (sem PAT)' }}</dd>
                    <dt>Destinatário Email:</dt>
                    <dd>{{ selectedRow.recipient_email || '-' }}</dd>
                    <dt>Destinatário SMS:</dt>
                    <dd>{{ selectedRow.recipient_phone || '-' }}</dd>
                    <dt>Enviada em:</dt>
                    <dd>{{ formatDateTime(selectedRow.sent_at) }}</dd>
                    <dt>Expira em:</dt>
                    <dd>{{ formatDateTime(selectedRow.expires_at) }}</dd>
                    <dt>Lembrete enviado:</dt>
                    <dd>{{ formatDateTime(selectedRow.reminder_sent_at) }}</dd>
                    <dt>Respondida em:</dt>
                    <dd>{{ formatDateTime(selectedRow.responded_at) }}</dd>
                    <dt>Estado:</dt>
                    <dd>{{ statusLabel(selectedRow.status) }}</dd>
                    <dt>Resposta:</dt>
                    <dd>
                        <span v-if="selectedRow.response === true">Aceite</span>
                        <span v-else-if="selectedRow.response === false">Recusada</span>
                        <span v-else>Pendente</span>
                    </dd>
                    <dt>Observações Cliente:</dt>
                    <dd>{{ selectedRow.observations || '-' }}</dd>
                    <dt>Dossier aprovado:</dt>
                    <dd>
                        <span v-if="selectedRow.dossier_aprovado === null || selectedRow.dossier_aprovado === undefined">-</span>
                        <span v-else>{{ selectedRow.dossier_aprovado ? 'Sim' : 'Não' }}</span>
                    </dd>
                    <dt>Dossier tabela1:</dt>
                    <dd>{{ selectedRow.dossier_tabela1 || '-' }}</dd>
                </dl>
            </div>
            <template #footer>
                <a
                    v-if="selectedRow && selectedRow.hash"
                    :href="getValidationUrl(selectedRow.hash)"
                    target="_blank"
                    class="p-button p-component p-button-warning p-mr-2"
                    style="text-decoration: none"
                >
                    <span class="p-button-icon p-button-icon-left pi pi-external-link"></span>
                    <span class="p-button-label">Abrir Link Validação</span>
                </a>
                <Button
                    v-if="selectedRow && selectedRow.pat_nopat"
                    label="Ver PAT"
                    icon="pi pi-box"
                    class="p-button p-button-info"
                    @click="goToPat(selectedRow.pat_nopat)"
                />
                <Button
                    v-if="selectedRow && !selectedRow.pat_nopat && selectedRow.dossier_exists"
                    label="Ver Dossier"
                    icon="pi pi-file"
                    class="p-button p-button-info"
                    @click="goToDossier(selectedRow)"
                />
                <Button
                    label="Fechar"
                    icon="pi pi-times"
                    class="p-button-text"
                    @click="detailsDialog = false"
                />
            </template>
        </Dialog>
    </div>
</template>

<script>
import technicalManagementService from '@/services/technical-management.service';
import ConfirmDialog from 'primevue/confirmdialog';

export default {
    name: "CommercialOfferDispatches",
    storageKey: "commercial_offer_dispatches_filters",
    components: {
        ConfirmDialog,
    },
    watch: {
        'filters.obrano'() { this.persistFilters(); },
        'filters.ndos'() { this.persistFilters(); },
        'filters.recipient'() { this.persistFilters(); },
        'filters.status'() { this.persistFilters(); },
        'filters.pat_nopat'() { this.persistFilters(); },
    },
    data() {
        return {
            list: [],
            loading: false,
            totalRecords: 0,
            currentPage: 1,
            perPage: 20,

            detailsDialog: false,
            selectedRow: null,

            filters: {
                obrano: null,
                ndos: 3,
                recipient: null,
                status: null,
                pat_nopat: null,
            },

            statusOptions: [
                { label: 'Pendente', value: 'pending' },
                { label: 'Com lembrete', value: 'reminded' },
                { label: 'Respondida', value: 'responded' },
                { label: 'Expirada', value: 'expired' },
            ],
        };
    },
    created() {
        this.restoreFilters();
        this.fetchData();
    },
    methods: {
        persistFilters() {
            const payload = {
                obrano: this.filters.obrano,
                ndos: this.filters.ndos,
                recipient: this.filters.recipient,
                status: this.filters.status,
                pat_nopat: this.filters.pat_nopat,
            };
            localStorage.setItem(this.$options.storageKey, JSON.stringify(payload));
        },
        restoreFilters() {
            const raw = localStorage.getItem(this.$options.storageKey);
            if (!raw) return;
            try {
                const saved = JSON.parse(raw);
                this.filters.obrano = saved.obrano ?? null;
                this.filters.ndos = saved.ndos ?? 3;
                this.filters.recipient = saved.recipient ?? null;
                this.filters.status = saved.status ?? null;
                this.filters.pat_nopat = saved.pat_nopat ?? null;
            } catch (e) {
                localStorage.removeItem(this.$options.storageKey);
            }
        },
        clearStoredFilters() {
            this.filters.obrano = null;
            this.filters.ndos = 3;
            this.filters.recipient = null;
            this.filters.status = null;
            this.filters.pat_nopat = null;
            localStorage.removeItem(this.$options.storageKey);
            this.fetchData();
        },
        fetchWithFilters() {
            this.persistFilters();
            this.fetchData();
        },
        async fetchData(event = {}) {
            try {
                this.loading = true;
                const page = event.page ? event.page + 1 : 1;
                const params = {
                    page,
                    per_page: event.rows ? event.rows : this.perPage,
                };
                if (this.filters.obrano) {
                    params.obrano = this.filters.obrano;
                }
                if (this.filters.ndos) {
                    params.ndos = this.filters.ndos;
                }
                if (this.filters.recipient) {
                    params.recipient = this.filters.recipient;
                }
                if (this.filters.status) {
                    params.status = this.filters.status;
                }
                if (this.filters.pat_nopat) {
                    params.pat_nopat = this.filters.pat_nopat;
                }

                const response = await technicalManagementService.getCommercialOfferDispatches(params);
                if (response && response.data) {
                    this.list = response.data;
                    this.totalRecords = response.meta ? response.meta.total : response.data.length;
                    this.currentPage = response.meta ? response.meta.current_page : 1;
                } else {
                    this.list = [];
                    this.totalRecords = 0;
                }
            } catch (error) {
                this.$toast.add({
                    severity: 'error',
                    summary: 'Erro',
                    detail: 'Erro ao carregar propostas',
                    life: 3000,
                });
            } finally {
                this.loading = false;
            }
        },
        viewDetails(row) {
            this.selectedRow = row;
            this.detailsDialog = true;
        },
        canExpire(row) {
            return row && (row.status === 'pending' || row.status === 'reminded');
        },
        askForExpire(row) {
            this.$confirm.require({
                group: 'commercialOfferDispatchesConfirmDialog',
                message: `Forçar expiração da proposta #${row.obrano}? A proposta será marcada como NÃO ACEITE e o responsável interno será notificado.`,
                header: 'Forçar Expiração',
                icon: 'pi pi-exclamation-triangle',
                acceptLabel: 'Sim, expirar',
                rejectLabel: 'Cancelar',
                acceptClass: 'p-button-danger',
                accept: async () => await this.expire(row),
                reject: () => {},
            });
        },
        async expire(row) {
            try {
                this.loading = true;
                const response = await technicalManagementService.expireCommercialOfferDispatch(row.id);
                if (response.status === 200) {
                    this.$toast.add({
                        severity: 'success',
                        summary: 'Sucesso',
                        detail: 'Proposta expirada com sucesso',
                        life: 3000,
                    });
                    await this.fetchData();
                } else {
                    const message = response.data && response.data.message
                        ? response.data.message
                        : 'Não foi possível expirar a proposta';
                    this.$toast.add({
                        severity: 'error',
                        summary: 'Erro',
                        detail: message,
                        life: 4000,
                    });
                }
            } catch (error) {
                this.$toast.add({
                    severity: 'error',
                    summary: 'Erro',
                    detail: 'Erro ao expirar proposta',
                    life: 3000,
                });
            } finally {
                this.loading = false;
            }
        },
        formatDateTime(value) {
            if (!value) return '-';
            const d = new Date(value);
            if (isNaN(d.getTime())) return '-';
            return d.toLocaleString('pt-PT', {
                day: '2-digit',
                month: '2-digit',
                year: 'numeric',
                hour: '2-digit',
                minute: '2-digit',
            });
        },
        statusLabel(status) {
            const map = {
                pending: 'Pendente',
                reminded: 'Com lembrete',
                responded: 'Respondida',
                expired: 'Expirada',
            };
            return map[status] || status || '-';
        },
        statusColor(status) {
            const map = {
                pending: '#6c757d',
                reminded: '#f59e0b',
                responded: '#10b981',
                expired: '#ef4444',
            };
            return map[status] || '#6c757d';
        },
        goToPat(patId) {
            if (!patId) return;
            this.$router.push(`/pats/${patId}`);
        },
        goToDossier(row) {
            if (!row || !row.dossier_id) return;
            this.$router.push(`/internal-dossiers/update/${row.dossier_id}`);
        },
        getValidationUrl(hash) {
            if (!hash) return '#';
            return window.location.origin + "/p/commercial/" + hash;
        },
    },
};
</script>

<style scoped>
.card {
    padding: 2rem;
}

h1 {
    margin-bottom: 1.5rem;
    color: #495057;
}

.p-field label {
    display: block;
    margin-bottom: 0.5rem;
    font-weight: 600;
}

dl {
    display: grid;
    grid-template-columns: 180px 1fr;
    gap: 0.5rem;
    margin: 0;
}

dt {
    font-weight: 600;
    color: #6c757d;
}

dd {
    margin: 0;
}

.p-gap-2 {
    gap: 0.5rem;
}

.clickable-item {
    cursor: pointer;
    color: #2196f3;
}
</style>
